import React from 'react'
import { Modal as ReactOverlayModal } from 'react-overlays'
import styled, { css } from 'styled-components'
import { PrimaryButton, OutlineButton } from '@chegg/fanta'
import { IconButton } from '@chegg-ui/button'
import { spacing } from '@chegg-ui/theme'
import { adjust, smallLayout } from '@chegg-wt/core/theme/theme'
import { HeadingReset } from '@chegg-wt/core/theme/sunkist/resets'
import { values } from '@chegg-wt/core/theme/sunkist/spacing'
import { Heading2, BodyLarge } from '@chegg-wt/core/theme/sunkist/typography'

interface ModalStyledProps {
  className?: string
  enableScroll: boolean
}

export const ModalWrapper = styled(({ enableScroll, ...props }) => (
  <ReactOverlayModal {...props} />
))<ModalStyledProps>`
  position: ${(props) => (props.enableScroll ? 'static' : 'fixed')};
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 3000;
  overflow-y: scroll;
`

export const PositioningWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
`

export const ModalContent = styled.div<{
  fullMobileModal: boolean
  fullWidth?: boolean
  maxWidth?: string
  noPadding?: boolean
  transparentBackground?: boolean
  maxHeight?: boolean
}>`
  position: relative;
  background-color: ${(props) =>
    props.transparentBackground ? `rgba(0, 0, 0, 0)` : props.theme.colors.white};
  padding: ${(props) => (props.noPadding ? 'none' : props.theme.spacing.lg)};
  margin: ${(props) => props.theme.spacing.sm};
  z-index: 3000;
  max-width: ${({ maxWidth }) => maxWidth || '600px'};
  ${(props) => props.maxHeight && `max-height: 100%;`}
  width: ${(props) => (props.fullWidth ? '100%' : 'unset')};
  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    ${({ fullMobileModal, maxHeight }) =>
      fullMobileModal &&
      !maxHeight &&
      `width: 100%;
      max-width: 100%;
      height: 100%
      `};
  }
`

export const Body = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

export const CloseButton = styled(IconButton)<{
  closeButtonColor?: string
  topSpace?: string
  isLoading?: boolean
  zIndex?: number
}>`
  position: absolute;
  top: ${(props) => (props.topSpace ? props.topSpace : 0)};
  right: 0;
  color: ${(props) => (props.closeButtonColor ? props.closeButtonColor : '')};
  z-index: ${(props) => (props.zIndex ? props.zIndex : 0)};
  ${(props) => props.isLoading && 'display: none'};
`

export const DefaultBackdrop = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  background-color: ${({ theme }) => theme.Colors.forFonts.fColor06};
  opacity: 0.5;
`

export const Heading = styled.h1`
  ${HeadingReset};
  ${Heading2};

  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    text-align: center;
  }
`

export const Message = styled.p`
  ${BodyLarge};

  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    text-align: center;
  }
`
export const CancelButton = styled(OutlineButton).attrs({
  isHoverAnimated: true,
  size: 'medium',
})`
  border-color: ${({ theme }) => theme.Colors.primaryColor};
  color: ${({ theme }) => theme.Colors.primaryColor};
  &:hover {
    border-color: ${({ theme }) => theme.Colors.forFonts.fColor16};
    background-color: ${({ theme }) => theme.Colors.forFonts.fColor16};
  }
  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    width: 100%;
  }
`

export const ConfirmButton = styled(PrimaryButton).attrs({
  isHoverAnimated: true,
  size: 'medium',
})`
  border-color: ${({ theme }) => theme.Colors.primaryColor};
  background-color: ${({ theme }) => theme.Colors.primaryColor};
  &:hover {
    border-color: ${({ theme }) => theme.Colors.forFonts.fColor16};
    background-color: ${({ theme }) => theme.Colors.forFonts.fColor16};
  }
  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    width: 100%;
  }
`

export const ButtonContainer = styled.div`
  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    display: none;
  }

  @media (min-width: ${({ theme }) => smallLayout(theme)}) {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    & > * {
      width: 186px;
    }

    & > * + * {
      margin-left: ${values.small};
    }
  }
`

export const ButtonContainerMobile = styled.div`
  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    display: flex;
    flex-direction: column;
    align-items: center;

    & > * {
      width: 100%;
    }

    & > * + * {
      margin-top: ${values.small};
    }
  }

  @media (min-width: ${({ theme }) => smallLayout(theme)}) {
    display: none;
  }
`

export const ButtonContainerResponsive = styled.div`
  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    display: flex;
    flex-direction: column;
    align-items: center;

    & > * {
      width: 100%;
    }

    & > * + * {
      margin-top: ${spacing.px16};
    }
  }

  @media (min-width: ${({ theme }) => smallLayout(theme)}) {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-start;

    & > * {
      width: 186px;
    }

    & > * + * {
      margin-right: ${spacing.px16};
    }
  }
`

export const Container = styled.div`
  width: 100%;

  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  & > * + * {
    margin-top: ${values.small};
  }
`

export const Input = styled.input`
  display: block;
  width: 100%;
  height: 34px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 100;
  line-height: 1.42857143;
  color: ${({ theme }) => theme.Colors.forInterface.interfaceColor16};
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  height: 50px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.Colors.forFonts.fColor05};
  &:focus {
    border-color: #0090ff;
    outline: 0;
  }
  &::-moz-placeholder {
    color: ${({ theme }) => theme.Colors.forInterface.interfaceColor22};
    opacity: 1;
  }
  &::-webkit-input-placeholder {
    color: ${({ theme }) => theme.Colors.forInterface.interfaceColor22};
  }
`

interface ButtonProps {
  inline?: boolean
  primary?: boolean
  newPrimary?: boolean
  lightGray?: boolean
  tertiary?: boolean
  blue?: boolean
  disabled?: boolean
}

export const LinkButton = styled.a<ButtonProps>`
  padding: 13px 27px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  font-size: ${(props) => props.theme.font.text.size.md};
  display: ${(props) => (props.inline ? 'inline-flex' : 'block')};
  width: ${(props) => (props.inline ? 'auto' : '100%')};
  align-items: center;
  font-weight: normal;
  text-align: center;
  vertical-align: middle;
  touch-action: manipulation;
  cursor: pointer;
  background-image: none;
  line-height: 1.42857143;
  border-radius: 3px;
  text-decoration: none;
  user-select: none;
  &:hover {
  }
  &:active {
    box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
  }
  &:active,
  &:focus {
    outline: none;
    text-decoration: none;
  }
  &:disabled,
  &.disabled {
    opacity: 0.3;
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: auto;
      opacity: 0.3;
      &:active {
        box-shadow: none;
      }
    `} &:focus {
    border-color: ${(props) => props.theme.colors.blue};
  }

  ${(props) =>
    props.primary &&
    css`
      border-color: ${props.theme.colors.orange};
      background-color: ${props.theme.colors.white};
      width: auto;
      text-decoration: none;
      color: ${props.theme.colors.orange};
      font-size: ${props.theme.font.text.size.md};
      border-radius: 3px;
      font-weight: 400;
      min-width: auto;
      &:hover {
        color: ${props.theme.colors.orangeDark};
        border-color: ${props.theme.colors.orangeDark};
        &:active {
          color: ${props.theme.colors.orangeDarker};
          border-color: ${props.theme.colors.orangeDarker};
        }
      }
      &:focus {
        color: ${props.theme.colors.orange};
        border-color: ${props.theme.colors.blue};
      }
    `};

  ${(props) =>
    props.newPrimary &&
    css`
      color: ${props.theme.colors.white};
      background: ${props.theme.colors.orange};
      &:hover,
      &:active,
      &:focus {
        color: ${props.theme.colors.white};
      }
    `};

  ${(props) =>
    props.lightGray &&
    css`
      color: ${props.theme.colors.greyDark};
      background: ${props.theme.colors.greyEEE};
      &:hover,
      &:active,
      &:focus {
        color: ${props.theme.colors.black};
      }
      &:hover {
        background: ${props.theme.colors.greyLight};
        &:active {
          background: ${props.theme.colors.grey};
        }
      }
    `};

  ${(props) =>
    props.blue &&
    css`
      border-color: ${props.theme.colors.blue};
      background-color: ${props.theme.colors.white};
      width: auto;
      padding: 6px 12px;
      border-width: 1px;
      text-decoration: none;
      color: ${props.theme.colors.blue};
      font-size: ${props.theme.font.text.size.md};
      border-radius: 3px;
      font-weight: 400;
      min-width: auto;
      &:hover {
        color: ${props.theme.colors.blue};
        border-color: ${props.theme.colors.blue};
        &:active {
          color: ${props.theme.colors.blue};
          border-color: ${props.theme.colors.blue};
        }
      }
      &:focus {
        color: ${props.theme.colors.blue};
        border-color: ${props.theme.colors.blue};
      }
    `};

  ${(props) =>
    props.tertiary &&
    css`
      color: ${props.theme.colors.black};
      background: ${props.theme.colors.greylighter};
      &:hover,
      &:active,
      &:focus {
        color: ${props.theme.colors.black};
      }
      &:hover {
        background: ${props.theme.colors.greyLight};
        &:active {
          background: ${props.theme.colors.grey};
        }
      }
    `};
`

const smallFrameLayout = ({ theme }) => theme.breakpoints[1]

export const DropdownTrigger = styled.button`
  height: 44px;
  width: 100%;
  border: 2px solid ${({ theme }) => theme.Colors.forInterface.interfaceColor13};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
  padding: 11px 14px;
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  @media (max-width: ${(props) => adjust(smallFrameLayout(props), -1)}) {
    width: 330px;
  }
`
